type VersionedV1<T> = {
  v: 1;
  value: T;
  updatedAt: number;
};

function safeNow(): number {
  return Date.now();
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

export function buildUserScopedKey(userEmail: string | null | undefined, featureKey: string): string {
  const scope = userEmail?.trim() ? encodeURIComponent(userEmail.trim()) : "anon";
  return `tpt-pref:${scope}:${featureKey}`;
}

export function safeLoadJson<T>(fullKey: string): T | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(fullKey);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function safeSaveJson(fullKey: string, value: unknown): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(fullKey, JSON.stringify(value));
  } catch {
    // ignore (quota/private mode)
  }
}

export function safeRemove(fullKey: string): void {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(fullKey);
  } catch {
    // ignore
  }
}

export function loadV1<T>(fullKey: string): T | null {
  const parsed = safeLoadJson<unknown>(fullKey);
  if (!isObject(parsed)) return null;
  if (parsed.v !== 1) return null;
  return (parsed as VersionedV1<T>).value ?? null;
}

export function saveV1<T>(fullKey: string, value: T): void {
  const payload: VersionedV1<T> = { v: 1, value, updatedAt: safeNow() };
  safeSaveJson(fullKey, payload);
}

export function loadEnumV1<T extends string>(
  fullKey: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const v = loadV1<unknown>(fullKey);
  return typeof v === "string" && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

export function saveEnumV1<T extends string>(fullKey: string, value: T): void {
  saveV1(fullKey, value);
}

export type SortPrefV1<SortBy extends string, SortOrder extends string> = {
  sortBy: SortBy;
  sortOrder: SortOrder;
};

export function loadSortPrefV1<SortBy extends string, SortOrder extends string>(
  fullKey: string,
  allowedSortBy: readonly SortBy[],
  allowedSortOrder: readonly SortOrder[],
  fallback: SortPrefV1<SortBy, SortOrder>,
): SortPrefV1<SortBy, SortOrder> {
  const v = loadV1<unknown>(fullKey);
  if (!isObject(v)) return fallback;
  const sortBy = v.sortBy;
  const sortOrder = v.sortOrder;
  const okSortBy = typeof sortBy === "string" && (allowedSortBy as readonly string[]).includes(sortBy);
  const okSortOrder =
    typeof sortOrder === "string" && (allowedSortOrder as readonly string[]).includes(sortOrder);
  if (!okSortBy || !okSortOrder) return fallback;
  return { sortBy: sortBy as SortBy, sortOrder: sortOrder as SortOrder };
}

export function saveSortPrefV1<SortBy extends string, SortOrder extends string>(
  fullKey: string,
  value: SortPrefV1<SortBy, SortOrder>,
): void {
  saveV1(fullKey, value);
}

export function migrateLegacyKeysToUserScopedV1(options: {
  userEmail: string | null | undefined;
  featureKey: string;
  legacyKeys: string[];
}): void {
  try {
    if (typeof window === "undefined") return;
    const scopedKey = buildUserScopedKey(options.userEmail, options.featureKey);
    if (window.localStorage.getItem(scopedKey)) return; // already set

    for (const legacyKey of options.legacyKeys) {
      const raw = window.localStorage.getItem(legacyKey);
      if (!raw) continue;
      // Store legacy raw as-is under v1 wrapper (best-effort JSON parse).
      let value: unknown = raw;
      try {
        value = JSON.parse(raw);
      } catch {
        // keep raw string
      }
      saveV1(scopedKey, value);
      return;
    }
  } catch {
    // ignore
  }
}

export function migrateAnonToUserScopedV1(options: {
  userEmail: string | null | undefined;
  featureKey: string;
}): void {
  if (!options.userEmail?.trim()) return;
  const anonKey = buildUserScopedKey(null, options.featureKey);
  const userKey = buildUserScopedKey(options.userEmail, options.featureKey);
  try {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(userKey)) return;
    const raw = window.localStorage.getItem(anonKey);
    if (!raw) return;
    window.localStorage.setItem(userKey, raw);
  } catch {
    // ignore
  }
}

